/**
 * 연식 코호트 — 오래된 설비가 더 고장나는가 (DESIGN §8.10).
 *
 * **가동률과 이용률을 한 축에 함께 그린다.** 둘 다 0~1 비율이라 축이 하나면
 * 충분하다 — 이중 축은 두 계열의 관계를 그리는 사람이 마음대로 정하게 되므로
 * 쓰지 않는다.
 *
 * **이용률을 함께 보는 이유가 이 화면의 핵심이다.** 노후기가 덜 쓰이면 고장도
 * 덜 드러난다 — 이용이 없으면 이상도 관측되지 않는다. 실측에서 이용률은
 * 2017년산 30.8% → 2026년산 43.2% 로 **연식과 단조로운데 가동률은 그렇지
 * 않다.** 두 선을 겹쳐 놓아야 "가동률이 좋은 것"과 "안 쓰여서 좋아 보이는 것"이
 * 갈린다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import type { Envelope, RankingData, RankingItem, YearCohortsData } from '@/lib/types'
import { useObservationDays } from '@/lib/window'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

/**
 * **판정은 서버가 한다** (`/metrics/year_cohorts`, §8.10.1).
 *
 * 처음에는 여기서 `year < 2000`(센티널)과 `30기 미만`(표본 부족) 둘로만 걸렀다.
 * 그 그물을 **둘 다 빠져나가는 경우**가 실재했다 — 2006년산 166기는 한 사업자
 * (뉴로모빌리티)의 입력 관행인데 화면은 20년 된 설비가 가동률 99.93% 라고
 * 말하고 있었다. 판정 규칙(사업자 집중도)은 서버에 한 곳으로 두고 여기서는
 * 그 결과를 쓴다 — 규칙이 두 곳에 있으면 갈라진다.
 */

export function AgingCohort() {
  const days = useObservationDays()
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['metrics/ranking', 'aging', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<RankingData>(
        '/metrics/ranking',
        toQueryFilters(filters),
        { entity: 'year' },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  })

  // 연식 코호트 판정. 모집단 전체 기준이라 필터를 태우지 않는다 (§8.10.1)
  const cq = useQuery({
    queryKey: ['metrics/year_cohorts'],
    queryFn: ({ signal }) =>
      fetchEnvelope<YearCohortsData>('/metrics/year_cohorts', { mode: 'standard' }, {}, signal),
    staleTime: 10 * 60 * 1000,
  })

  const env = q.data as Envelope<RankingData> | undefined
  const verdictOf = useMemo(() => {
    const m = new Map<string, { verdict: string; reason: string }>()
    for (const c of (cq.data as Envelope<YearCohortsData> | undefined)?.data.cohorts ?? []) {
      m.set(String(c.year), { verdict: c.verdict, reason: c.reason })
    }
    return m
  }, [cq.data])

  const { cohorts, aside } = useMemo(() => {
    // `excluded` 는 **가동률 비교 가능성** 기준으로 갈린 것이라 여기서는 합쳐
    // 쓴다 — 연식 코호트는 사업자 화이트리스트 판정 대상이 아니다
    const all: RankingItem[] = [...(env?.data.ranking ?? []), ...(env?.data.excluded ?? [])]
    const named = all.filter((r) => r.entity_id)
    // 판정이 아직 안 왔으면 **아무것도 내리지 않는다** — 판정 없이 거르면
    // 어떤 기준으로 뺐는지 화면이 설명할 수 없다
    const usable = (r: RankingItem) =>
      (verdictOf.get(r.entity_id)?.verdict ?? 'cohort') === 'cohort'
    return {
      cohorts: named.filter(usable).sort((a, b) => Number(a.entity_id) - Number(b.entity_id)),
      aside: named.filter((r) => !usable(r))
        .sort((a, b) => (a.entity_id ?? '').localeCompare(b.entity_id ?? '')),
    }
  }, [env, verdictOf])

  /** 가동률이 연식과 단조로운가 — 이 화면이 가장 먼저 반증하는 통념 */
  const worst = useMemo(
    () =>
      cohorts.reduce<RankingItem | null>(
        (w, r) => (w == null || (r.availability ?? 1) < (w.availability ?? 1) ? r : w),
        null,
      ),
    [cohorts],
  )
  const oldest = cohorts[0]

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const c2 = cssVar('--c-cat-2', '#a35200')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const canvas = cssVar('--c-canvas', '#ffffff')

    return {
      grid: { left: 48, right: 16, top: 30, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const arr = Array.isArray(params) ? params : [params]
          const i = (arr[0] as { dataIndex: number }).dataIndex
          const r = cohorts[i]
          if (!r) return ''
          return `<b>${r.entity_id}년산</b> · ${int(r.n_chargers)}기<br/>가동률 ${pct(
            r.availability,
          )}<br/>이용률 ${pct(r.utilization)}<br/>커버리지 ${pct(r.coverage)}`
        },
      },
      xAxis: {
        type: 'category',
        data: cohorts.map((r) => r.entity_id),
        name: '설치 연식',
        nameLocation: 'middle',
        nameGap: 24,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3 },
      },
      // **축은 하나다.** 두 계열 모두 0~1 비율이므로 이중 축을 쓸 이유가 없다
      yAxis: {
        type: 'value',
        min: 0,
        max: 1,
        name: '비율',
        nameTextStyle: { fontSize: 10, color: ink3, align: 'left' },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: (v: number) => `${(v * 100).toFixed(0)}%` },
      },
      series: [
        {
          name: '가동률',
          type: 'line',
          data: cohorts.map((r) => r.availability),
          lineStyle: { width: 2, color: c1 },
          itemStyle: { color: c1, borderWidth: 2, borderColor: canvas },
          symbolSize: 8,
        },
        {
          name: '이용률',
          type: 'line',
          data: cohorts.map((r) => r.utilization ?? null),
          lineStyle: { width: 2, color: c2, type: 'dashed' },
          itemStyle: { color: c2, borderWidth: 2, borderColor: canvas },
          symbol: 'diamond',
          symbolSize: 9,
        },
      ],
    }
  }, [cohorts])

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">설치 연식별 가동률·이용률</caption>
        <thead>
          <tr>
            <th scope="col">연식</th>
            <th scope="col">충전기</th>
            <th scope="col">가동률</th>
            <th scope="col">이용률</th>
            <th scope="col">커버리지</th>
          </tr>
        </thead>
        <tbody>
          {cohorts.map((r) => (
            <tr key={r.entity_id}>
              <th scope="row">{r.entity_id}</th>
              <td className="num">{int(r.n_chargers)}</td>
              <td className="num">{pct(r.availability)}</td>
              <td className="num">{pct(r.utilization)}</td>
              <td className="num">{pct(r.coverage)}</td>
            </tr>
          ))}
          {aside.map((r) => (
            <tr key={r.entity_id} className="is-na">
              <th scope="row">{r.entity_id}</th>
              <td className="num">{int(r.n_chargers)}</td>
              {/* 값을 지우지 않는다 — 이 설비들은 실재하고 동작한다 */}
              <td className="num">{pct(r.availability)}</td>
              <td className="num">{pct(r.utilization)}</td>
              <td>{verdictOf.get(r.entity_id)?.reason ?? '나이 신호로 쓸 수 없음'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="설치 연식별 가동률과 이용률"
      question="오래된 설비가 더 고장나는가 — 그리고 덜 쓰여서 좋아 보이는 것은 아닌가"
      meta={env?.meta}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={cohorts.length === 0}
      onRetry={() => void q.refetch()}
      headline={
        worst && oldest ? (
          <>
            <span className="num">{worst.entity_id}년산</span>
            <span className="cframe__headnote">
              가장 낮은 가동률 {pct(worst.availability, 2)}
              {worst.entity_id !== oldest.entity_id && ' — 가장 오래된 코호트가 아니다'}
            </span>
          </>
        ) : null
      }
      legend={[
        { label: '가동률', color: cssVar('--c-cat-1', '#0066cc'), pattern: 'line' },
        { label: '이용률', color: cssVar('--c-cat-2', '#a35200'), pattern: 'line' },
      ]}
      encoding={[
        { role: '가로축', what: '설치 연식' },
        { role: '세로축', what: '비율 (0~100%) — 두 계열이 같은 축을 쓴다' },
        { role: '실선 · 원', what: '가동률' },
        { role: '파선 · 마름모', what: '이용률' },
      ]}
      reading="같은 시점에 서 있는 연식 코호트를 나란히 견줍니다. 시간에 따른 열화를 그린 것이 아닙니다."
      notes={[
        {
          label: '시계열이 아니다',
          text:
            `관측 창이 ${days ?? '—'}일입니다. 잴 수 있는 것은 「지금 시점의 코호트 간 차이」뿐이고 열화 속도가 아닙니다. 이 화면은 「노후화 속도」를 말하지 않습니다.`,
        },
        {
          label: '생존 편향',
          text:
            '철거된 노후기는 모집단에 없습니다. 살아남은 노후기만 보므로 노후화의 영향이 체계적으로 과소추정됩니다 — 오래된 코호트의 대수는 그 해 보급량이 아니라 「지금까지 남은 수」입니다.',
        },
        {
          label: '교란',
          text:
            '연식별로 제조사·설치 환경·충전기 타입 구성이 다릅니다. 특정 연식의 하락이 노후 탓인지 그 해 대량 보급된 사업자 탓인지 이 그래프만으로는 구분되지 않습니다 — 왼쪽 필터로 제조사나 사업자를 하나로 좁히고 다시 보세요. 이 화면이 필터를 받는 이유입니다.',
        },
        {
          label: '이용률을 함께 보는 이유',
          text:
            '이용이 없으면 이상도 관측되지 않습니다. 이용률이 낮은 코호트의 높은 가동률은 「멀쩡하다」가 아니라 「안 쓰여서 고장이 드러나지 않았다」일 수 있습니다.',
        },
        ...(aside.length > 0
          ? [
              {
                label: '별도 관리',
                text:
                  `${aside.length}개 연식은 차트에서 빼고 표 보기 아래쪽에 사유와 함께 남겼습니다. ` +
                  '버린 것이 아닙니다 — 그 충전기들은 실재하고 동작합니다. ' +
                  '한 사업자가 그 연식을 거의 다 차지해 **설비의 나이로 읽을 수 없을 뿐**입니다.',
              },
            ]
          : []),
      ]}
      table={table}
    >
      <EChart option={option} height={280} ariaLabel="설치 연식별 가동률과 이용률 꺾은선그래프" />
    </ChartFrame>
  )
}
